import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Droplet, Plus, Undo2 } from "lucide-react";
import { api } from "../api/client";

const HydrationWidget = ({ token }) => {
  const [hydration, setHydration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHydration();
  }, []);

  const fetchHydration = async () => {
    try {
      const res = await api("get", "/hydration/today", null, token);
      setHydration(res.data);
    } catch (err) {
      console.error("Hydration fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const addWater = async (amount) => {
    try {
      await api("post", "/hydration/log", { amount }, token);
      // Pełne odświeżenie zamiast łatania dwóch pól: dzięki temu lista wpisów
      // (a więc i możliwość cofnięcia) jest zawsze zgodna z tym, co w bazie.
      await fetchHydration();
      toast.success(`+${amount}ml`);
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  // Wody dało się tylko dolać. Endpoint kasujący wpis istniał od dawna i nie
  // miał żadnego przycisku, więc pomyłki (np. dwa kliknięcia) nie dało się cofnąć.
  const undoLast = async () => {
    const last = (hydration?.logs || [])[hydration.logs.length - 1];
    if (!last) return;
    try {
      await api("delete", `/hydration/log/${last.id}`, null, token);
      await fetchHydration();
      toast.success(`Cofnięto ${last.amount}ml`);
    } catch (err) {
      toast.error("Nie udało się cofnąć");
    }
  };

  if (loading || !hydration) return null;

  return (
    <Card className="hydration-widget" data-testid="hydration-widget">
      <CardContent>
        <div className="hydration-header">
          <Droplet size={20} />
          <span>Nawodnienie</span>
        </div>
        <div className="hydration-progress">
          <div className="hydration-circle">
            <svg viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" className="hydration-bg" />
              <circle
                cx="50"
                cy="50"
                r="45"
                className="hydration-fill"
                style={{
                  strokeDasharray: `${hydration.progress_percent * 2.83} 283`,
                  stroke: hydration.progress_percent >= 100 ? 'var(--success)' : 'var(--primary)'
                }}
              />
            </svg>
            <div className="hydration-value">
              <span className="amount">{(hydration.total / 1000).toFixed(1)}</span>
              <span className="unit">L</span>
            </div>
          </div>
          <div className="hydration-goal">
            cel: {(hydration.goal / 1000).toFixed(1)}L
          </div>
        </div>
        <div className="hydration-buttons">
          <Button variant="outline" size="sm" onClick={() => addWater(250)} data-testid="add-250ml">
            <Plus size={14} /> 250ml
          </Button>
          <Button variant="outline" size="sm" onClick={() => addWater(500)} data-testid="add-500ml">
            <Plus size={14} /> 500ml
          </Button>
          {(hydration.logs || []).length > 0 && (
            <Button variant="ghost" size="sm" onClick={undoLast} data-testid="undo-water"
                    title="Cofnij ostatni wpis">
              <Undo2 size={14} />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HydrationWidget;
